import './Table.scss';
import React, { useEffect, useState } from 'react';
import { ColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, PaginationState, useReactTable } from '@tanstack/react-table';
import { Card } from 'components/Card/Card';
import { Loader } from 'components/Loader/Loader';

type TableProps = {
    title?: string;
    data: any[];
    columns: any[];
    columnHelper: ColumnHelper<any>;
    className?: string;
    selectable?: boolean;
    pageSize?: number;
    pagination?: boolean;
    isLoading?: boolean;
    search?: string;
    searchableFields?: string[];
};

export default function Table({
    title,
    data,
    columns,
    columnHelper,
    className,
    selectable,
    pageSize = 20,
    isLoading = false,
    pagination = true,
    search,
    searchableFields
}: TableProps): JSX.Element {
    const [paginationState, setPaginationState] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: pageSize
    });
    const tableColumns = [...columns];

    const filteredData = React.useMemo(() => {
        if (!search) return data;

        return data.filter((item) => {
            if (searchableFields && searchableFields.length > 0) {
                return searchableFields.some((field) => {
                    const value = item[field];
                    if (typeof value === 'string') {
                        return value.toLowerCase().includes(search.toLowerCase());
                    }
                    if (typeof value === 'number') {
                        return value.toString().includes(search);
                    }
                    return false;
                });
            } else {
                return Object.values(item).some((value) => {
                    if (typeof value === 'string') {
                        return value.toLowerCase().includes(search.toLowerCase());
                    }
                    if (typeof value === 'number') {
                        return value.toString().includes(search);
                    }
                    return false;
                });
            }
        });
    }, [data, search, searchableFields]);

    const table = useReactTable({
        data: pagination ? filteredData : filteredData.slice(0, pageSize),
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        ...(pagination
            ? {
                  getPaginationRowModel: getPaginationRowModel(),
                  onPaginationChange: setPaginationState,
                  state: {
                      pagination: paginationState
                  },
                  autoResetPageIndex: false
              }
            : {})
    });

    const renderPages = () => {
        return (
            <>
                page
                <select
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                        table.setPageIndex(Number(e.target.value) - 1);
                    }}
                >
                    {Array.from({ length: table.getPageCount() }, (_, i) => i + 1).map((pageNumber) => (
                        <option key={pageNumber} value={pageNumber}>
                            {pageNumber}
                        </option>
                    ))}
                </select>
                sur {table.getPageCount()}
            </>
        );
    };

    return (
        <Card title={title} className={className}>
            <div className="table">
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup, i) => {
                            return (
                                <tr key={i}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                id={header.id}
                                                key={header.id}
                                                style={
                                                    header.column.columnDef.minSize || header.column.columnDef.maxSize
                                                        ? {
                                                              width: header.getSize(),
                                                              minWidth: header.column.columnDef.minSize,
                                                              maxWidth: header.column.columnDef.maxSize
                                                          }
                                                        : {}
                                                }
                                            >
                                                {' '}
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={
                                                    cell.column.columnDef.minSize || cell.column.columnDef.maxSize
                                                        ? {
                                                              width: cell.column.getSize(),
                                                              minWidth: cell.column.columnDef.minSize,
                                                              maxWidth: cell.column.columnDef.maxSize
                                                          }
                                                        : {}
                                                }
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {isLoading && <Loader />}
                {pagination && table.getPageCount() > 1 && (
                    <div className="pagination">
                        <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                            {'<'}
                        </button>
                        <span className="pages">{renderPages()}</span>
                        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                            {'>'}
                        </button>
                    </div>
                )}
            </div>
        </Card>
    );
}
